// Trip planner route ordering (idea from the wine-route-finder reference
// app): greedy nearest-neighbor over straight-line distance. Good enough
// for a day-trip stop order; actual driving happens in Google Maps via
// the handoff link.

export function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// start: {lat, lng}; producers: [{latitude, longitude, ...}]; returns the
// planned stops in visiting order. Candidate pool is the 2n nearest to the
// start so the route stays a local loop instead of sprawling.
export function planTripRoute(start, producers, numStops) {
  const usable = producers.filter(
    (p) => p.latitude != null && p.longitude != null
      && Number.isFinite(Number(p.latitude)) && Number.isFinite(Number(p.longitude)),
  );
  if (!usable.length || numStops < 1) return [];

  const pool = usable
    .map((p) => ({ p, d: haversineKm(start.lat, start.lng, Number(p.latitude), Number(p.longitude)) }))
    .sort((a, b) => a.d - b.d)
    .slice(0, Math.min(numStops * 2, usable.length))
    .map((x) => x.p);

  const route = [];
  let cur = { lat: start.lat, lng: start.lng };
  const unvisited = [...pool];
  while (route.length < numStops && unvisited.length) {
    let best = 0;
    let bestD = Infinity;
    for (let i = 0; i < unvisited.length; i++) {
      const d = haversineKm(cur.lat, cur.lng, Number(unvisited[i].latitude), Number(unvisited[i].longitude));
      if (d < bestD) { bestD = d; best = i; }
    }
    const next = unvisited.splice(best, 1)[0];
    route.push(next);
    cur = { lat: Number(next.latitude), lng: Number(next.longitude) };
  }
  return route;
}

export function tripDistanceKm(start, route) {
  let km = 0;
  let cur = { lat: start.lat, lng: start.lng };
  for (const stop of route) {
    km += haversineKm(cur.lat, cur.lng, Number(stop.latitude), Number(stop.longitude));
    cur = { lat: Number(stop.latitude), lng: Number(stop.longitude) };
  }
  return km;
}

// Route-cache key, EXACTLY the previous planner's scheme so its seeded
// rows still hit: base64 of "City, MI-<count>-<sorted names>" with
// /+= stripped. cityLabel must include the suffix (", MI").
export function routeHash(cityLabel, count, wineryNames) {
  const input = `${cityLabel}-${count}-${[...wineryNames].sort().join(',')}`;
  const b64 = typeof btoa !== 'undefined'
    ? btoa(input)
    : Buffer.from(input, 'binary').toString('base64');
  return b64.replace(/[/+=]/g, '');
}

// One Mapbox Directions call for the whole loop: real driving geometry
// ([lat,lng] pairs) + total meters. Returns null on any failure — the
// planner falls back to straight lines and skips the cache save.
export async function fetchDrivingRoute(token, start, route) {
  if (!token || !route.length) return null;
  const pts = [
    `${start.lng},${start.lat}`,
    ...route.map((p) => `${Number(p.longitude)},${Number(p.latitude)}`),
  ].join(';');
  try {
    const res = await fetch(`https://api.mapbox.com/directions/v5/mapbox/driving/${pts}?access_token=${token}&geometries=geojson&overview=full`);
    if (!res.ok) return null;
    const data = await res.json();
    const r = data.routes?.[0];
    if (!r?.geometry?.coordinates?.length) return null;
    return {
      geometry: r.geometry.coordinates.map(([lng, lat]) => [lat, lng]),
      distanceM: r.distance,
    };
  } catch {
    return null;
  }
}

// Waypoints resolve to the real Google business, not a dropped pin:
// street address when the route cache carried one, else winery name +
// town, and bare coordinates only as a last resort.
const stopToken = (p) => {
  if (p.gmaps_address) return p.gmaps_address;
  if (p.name) return [p.name, p.ort, 'MI'].filter(Boolean).join(', ');
  return `${Number(p.latitude)},${Number(p.longitude)}`;
};

export function googleMapsDirUrl(start, route) {
  const pts = [`${start.lat},${start.lng}`, ...route.map(stopToken)];
  return `https://www.google.com/maps/dir/${pts.map(encodeURIComponent).join('/')}`;
}
